import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaPlus, FaSearch, FaStar, FaRegStar, FaFolder, FaFolderPlus,
  FaEllipsisV, FaTrash, FaEdit, FaComment, FaChevronDown, FaChevronRight,
  FaChevronLeft, FaTimes, FaHistory, FaMicrochip, FaArrowUp, FaCopy,
  FaCheck, FaRedo, FaExclamationTriangle, FaLock,
} from 'react-icons/fa';
import {
  getAllChats, saveChat, deleteChatDb,
  getAllProjects, saveProjectDb, deleteProjectDb,
} from './askAIDb';


const uid = () => `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
const truncate = (str, n) => (str.length > n ? str.slice(0, n - 1) + '…' : str);
const SIDEBAR_WIDTH = 320;

// ── Design tokens ─────────────────────────────────────────────────────────
// Kept in the family of the existing ASPIREdeck dark UI (blue primary,
// amber for bookmarks, violet for projects) but tuned for contrast,
// consistency, and a calmer overall palette.
const theme = {
  bg: 'transparent',
  bgElevated: 'transparent',
  panel: 'rgba(255,255,255,0.04)',
  panelSolid: 'rgba(17, 24, 48, 0.72)',
  border: 'rgba(255,255,255,0.07)',
  borderStrong: 'rgba(255,255,255,0.14)',
  textPrimary: '#e9edf7',
  textSecondary: '#98a2ba',
  textTertiary: '#5b6480',
  accent: '#3aa7f5',
  accentSoft: 'rgba(58,167,245,0.12)',
  accentBorder: 'rgba(58,167,245,0.38)',
  gradient: 'linear-gradient(135deg, #0284e8 0%, #22c3ff 100%)',
  amber: '#f0a83c',
  amberSoft: 'rgba(240,168,60,0.12)',
  violet: '#b295fb',
  violetSoft: 'rgba(178,149,251,0.12)',
  danger: '#f87171',
  dangerSoft: 'rgba(248,113,113,0.1)',
  radiusSm: '7px',
  radiusMd: '11px',
  radiusLg: '16px',
};

function groupChatsByDate(chats) {
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(new Date());
  const yesterday = today - 86400000;
  const weekAgo = today - 7 * 86400000;
  const groups = { Today: [], Yesterday: [], 'Previous 7 Days': [], Older: [] };
  chats.forEach((c) => {
    const d = startOfDay(new Date(c.updatedAt));
    if (d === today) groups.Today.push(c);
    else if (d === yesterday) groups.Yesterday.push(c);
    else if (d >= weekAgo) groups['Previous 7 Days'].push(c);
    else groups.Older.push(c);
  });
  return Object.entries(groups).filter(([, list]) => list.length > 0);
}

const menuItemStyle = {
  display: 'flex', alignItems: 'center', gap: '9px', width: '100%', padding: '10px 13px',
  background: 'none', border: 'none', color: '#ccd3e0', fontSize: '0.83rem', textAlign: 'left',
  cursor: 'pointer', borderRadius: '8px', transition: 'background 0.12s ease',
};

const secondaryBtnStyle = {
  padding: '9px 16px', background: 'rgba(255,255,255,0.06)', color: theme.textPrimary,
  border: `1px solid ${theme.border}`, borderRadius: theme.radiusSm, cursor: 'pointer',
  fontSize: '0.85rem', fontWeight: 600,
};

// ── Global styles: scrollbars, animation, responsive overlay, a11y ────────
const GlobalStyles = () => (
  <style>{`
    .askai-scroll::-webkit-scrollbar,
    .askai-textarea::-webkit-scrollbar { width: 6px; }
    .askai-scroll::-webkit-scrollbar-track,
    .askai-textarea::-webkit-scrollbar-track { background: transparent; }
    .askai-scroll::-webkit-scrollbar-thumb,
    .askai-textarea::-webkit-scrollbar-thumb {
      background: rgba(58,167,245,0.3); border-radius: 10px;
    }
    .askai-scroll::-webkit-scrollbar-thumb:hover,
    .askai-textarea::-webkit-scrollbar-thumb:hover { background: rgba(58,167,245,0.55); }
    .askai-scroll, .askai-textarea {
      scrollbar-width: thin;
      scrollbar-color: rgba(58,167,245,0.3) transparent;
    }

    .askai-root button, .askai-root input, .askai-root textarea {
      font-family: inherit;
    }
    .askai-root :focus-visible {
      outline: 2px solid ${theme.accent};
      outline-offset: 2px;
      border-radius: 4px;
    }

    @keyframes askaiFadeUp {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes askaiPopIn {
      from { opacity: 0; transform: scale(0.97); }
      to { opacity: 1; transform: scale(1); }
    }
    @keyframes askaiPulseRing {
      0% { box-shadow: 0 0 0 0 rgba(58,167,245,0.45); }
      70% { box-shadow: 0 0 0 8px rgba(58,167,245,0); }
      100% { box-shadow: 0 0 0 0 rgba(58,167,245,0); }
    }
    @keyframes askaiDot {
      0%, 80%, 100% { opacity: 0.25; transform: translateY(0); }
      40% { opacity: 1; transform: translateY(-2px); }
    }
    .askai-msg-enter { animation: askaiFadeUp 0.28s ease both; }
    .askai-thinking-avatar { animation: askaiPulseRing 1.8s ease-out infinite; }
    .askai-dot { animation: askaiDot 1.3s ease-in-out infinite; }
    .askai-dot:nth-child(2) { animation-delay: 0.15s; }
    .askai-dot:nth-child(3) { animation-delay: 0.3s; }

    .askai-modal-backdrop {
      position: fixed; inset: 0; background: rgba(6,9,18,0.65);
      backdrop-filter: blur(2px);
      display: flex; align-items: center; justify-content: center; z-index: 1400;
      animation: askaiFadeUp 0.15s ease both;
    }
    .askai-modal {
      background: ${theme.panelSolid}; padding: 26px; border-radius: ${theme.radiusLg};
      width: 400px; max-width: 90%; border: 1px solid ${theme.border};
      box-shadow: 0 20px 60px rgba(0,0,0,0.45);
      animation: askaiPopIn 0.18s ease both;
    }

    .askai-backdrop { display: none; }

    .askai-sidebar {
      width: 0; overflow: hidden; flex-shrink: 0;
      transition: width 0.22s ease;
      background: ${theme.bgElevated};
      border-left: 1px solid transparent;
    }
    .askai-sidebar.open {
      width: ${SIDEBAR_WIDTH}px;
      border-left: 1px solid ${theme.border};
    }

    @media (max-width: 860px) {
      .askai-sidebar.open {
        position: fixed; top: 0; right: 0; bottom: 0;
        width: min(${SIDEBAR_WIDTH}px, 86vw);
        z-index: 60;
        box-shadow: -16px 0 44px rgba(0,0,0,0.55);
      }
      .askai-backdrop { display: block; }
      .askai-suggestion-row { max-width: 100% !important; }
    }

    @media (prefers-reduced-motion: reduce) {
      .askai-msg-enter, .askai-thinking-avatar, .askai-dot,
      .askai-modal-backdrop, .askai-modal {
        animation: none !important;
      }
      .askai-sidebar, .askai-scroll * { transition: none !important; }
    }
  `}</style>
);

// ── Reusable confirm dialog ────────────────────────────────────────────────
function ConfirmDialog({ title, description, confirmLabel, tone = 'danger', onCancel, onConfirm }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onCancel]);

  const toneColor = tone === 'danger' ? theme.danger : theme.amber;

  return (
    <div className="askai-modal-backdrop" onClick={onCancel} role="presentation">
      <div
        className="askai-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="askai-modal-title"
      >
        <h3 id="askai-modal-title" style={{ margin: '0 0 10px', color: toneColor, fontSize: '1.05rem' }}>{title}</h3>
        <p style={{ color: theme.textSecondary, fontSize: '0.9rem', lineHeight: 1.55, margin: '0 0 22px' }}>{description}</p>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={secondaryBtnStyle}>Cancel</button>
          <button
            onClick={onConfirm}
            style={{
              padding: '9px 16px', background: toneColor, color: '#151222',
              border: 'none', borderRadius: theme.radiusSm, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── AI avatar: the page's one signature element — a small on-device chip
// that gently pulses while the model is thinking, tying the visual language
// back to "this runs on your own hardware, not the cloud". ─────────────────
function DeviceAvatar({ thinking }) {
  return (
    <div
      className={thinking ? 'askai-thinking-avatar' : ''}
      style={{
        width: '26px', height: '26px', borderRadius: '8px', flexShrink: 0,
        background: theme.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#08111f', boxShadow: '0 2px 8px rgba(3,140,232,0.3)',
      }}
      aria-hidden="true"
    >
      <FaMicrochip size={12} />
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: theme.textTertiary, fontSize: '0.88rem' }}>
      <span>Thinking on device</span>
      <span style={{ display: 'inline-flex', gap: '3px' }} aria-hidden="true">
        <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.textTertiary, display: 'inline-block' }} />
        <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.textTertiary, display: 'inline-block' }} />
        <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: theme.textTertiary, display: 'inline-block' }} />
      </span>
    </div>
  );
}

// ── A single Q&A exchange ───────────────────────────────────────────────────
function ChatMessage({ entry, onRetry, isReadOnly }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!entry.answer) return;
    try {
      await navigator.clipboard.writeText(entry.answer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable — fail silently, nothing to recover.
    }
  };

  return (
<div className="askai-msg-enter askai-message" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div className="askai-bubble-question" style={{
  alignSelf: 'flex-end', maxWidth: '78%', background: theme.gradient, color: '#08111f',
  padding: '10px 15px', borderRadius: '14px 14px 3px 14px', fontSize: '0.92rem',
  lineHeight: 1.5, fontWeight: 500,
}}>
  {entry.question}
</div>

     <div className="askai-bubble-answer-row" style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', maxWidth: '90%' }}>
  <DeviceAvatar thinking={!entry.answer && !entry.error} />
  <div className="askai-bubble-answer" style={{
    flex: 1, minWidth: 0, background: theme.panelSolid, border: `1px solid ${theme.border}`,
    padding: '13px 15px', borderRadius: '3px 14px 14px 14px', color: theme.textPrimary,
  }}>
          {entry.error ? (
            <div>
              <p style={{ margin: '0 0 10px', color: theme.danger, fontSize: '0.9rem', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                <FaExclamationTriangle style={{ marginTop: '3px', flexShrink: 0 }} />
                <span>{entry.error}</span>
              </p>
              {!isReadOnly && (
                <button
                  onClick={() => onRetry(entry.id, entry.question)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px', background: theme.dangerSoft,
                    border: `1px solid rgba(248,113,113,0.3)`, color: theme.danger, borderRadius: theme.radiusSm,
                    padding: '6px 11px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <FaRedo size={10} /> Try again
                </button>
              )}
            </div>
          ) : entry.answer ? (
            <>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6, fontSize: '0.94rem' }}>{entry.answer}</p>

              {entry.sources?.length > 0 && (
                <div style={{ marginTop: '12px', paddingTop: '11px', borderTop: `1px solid ${theme.border}` }}>
                  <div style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.accent, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Sources
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {entry.sources.map((s) => (
                      <span key={s.chunk_id} style={{
                        fontSize: '0.8rem', color: theme.textSecondary, background: 'rgba(255,255,255,0.04)',
                        border: `1px solid ${theme.border}`, borderRadius: '7px', padding: '4px 10px',
                      }}>
                        {s.original_name || 'document'}{s.subject ? ` · ${s.subject}` : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
                <button
                  onClick={handleCopy}
                  aria-label={copied ? 'Copied' : 'Copy answer'}
                  title={copied ? 'Copied' : 'Copy answer'}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none',
                    color: copied ? theme.accent : theme.textTertiary, fontSize: '0.78rem', cursor: 'pointer', padding: '3px 4px',
                  }}
                >
                  {copied ? <FaCheck size={11} /> : <FaCopy size={11} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </>
          ) : (
            <ThinkingBubble />
          )}
        </div>
      </div>
    </div>
  );
}

const ScrollbarStyles = GlobalStyles; // kept for naming continuity elsewhere in the app

export default function AskAIWorkspace({ deviceUrl, userId, isReadOnly, onOpenDeviceManager }) {
  const [chats, setChats] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [question, setQuestion] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsedProjects, setCollapsedProjects] = useState({});
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [showNewProjectInput, setShowNewProjectInput] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [moveMenuFor, setMoveMenuFor] = useState(null);
  const [confirmDeleteChat, setConfirmDeleteChat] = useState(null);
  const [confirmDeleteProject, setConfirmDeleteProject] = useState(null);

  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const menuRef = useRef(null);

  // ── Load chats + projects ──
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [c, p] = await Promise.all([getAllChats(userId), getAllProjects(userId)]);
      if (cancelled) return;
      const sorted = c.sort((a, b) => b.updatedAt - a.updatedAt);
      setChats(sorted);
      setProjects(p.sort((a, b) => a.name.localeCompare(b.name)));
      setActiveChatId(sorted[0]?.id || null);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [userId]);

  // ── Close dropdown menus on outside click / Escape ──
  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpenFor(null);
        setMoveMenuFor(null);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') { setMenuOpenFor(null); setMoveMenuFor(null); }
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  const activeChat = useMemo(() => chats.find((c) => c.id === activeChatId) || null, [chats, activeChatId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [activeChat?.messages, asking]);

  // ── Auto-grow the composer up to a max height, then let it scroll ──
  const resizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  useEffect(() => { resizeTextarea(); }, [question, resizeTextarea]);

  const persistChat = useCallback((chat) => { saveChat(userId, chat).catch(() => {}); }, [userId]);

  const updateChat = useCallback((chatId, updater) => {
    setChats((prev) => {
      const next = prev.map((c) => (c.id === chatId ? { ...updater(c), updatedAt: Date.now() } : c));
      const updated = next.find((c) => c.id === chatId);
      if (updated) persistChat(updated);
      return next;
    });
  }, [persistChat]);

  const createNewChat = () => {
    if (isReadOnly) return;
    const chat = { id: uid(), title: 'New chat', projectId: null, bookmarked: false, createdAt: Date.now(), updatedAt: Date.now(), messages: [] };
    setChats((prev) => [chat, ...prev]);
    setActiveChatId(chat.id);
    persistChat(chat);
    setQuestion('');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  // Shared send logic used by both a fresh question and a retry, so a retry
  // reuses the existing message slot instead of creating a duplicate.
  const sendQuestion = useCallback(async (chatId, msgId, q) => {
    setAsking(true);
    try {
      const res = await fetch(`${deviceUrl}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, top_k: 5 }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `Device responded with status ${res.status}`);
      updateChat(chatId, (c) => ({
        ...c,
        messages: c.messages.map((m) => (m.id === msgId ? { ...m, answer: data.answer || 'No answer returned.', sources: data.sources || [], error: null } : m)),
      }));
    } catch (err) {
      const message = err.message?.includes('fetch')
        ? "Couldn't reach your device. Check it's on the same Wi-Fi and try again."
        : err.message || 'Something went wrong.';
      updateChat(chatId, (c) => ({
        ...c,
        messages: c.messages.map((m) => (m.id === msgId ? { ...m, error: message, answer: '' } : m)),
      }));
    } finally {
      setAsking(false);
      textareaRef.current?.focus();
    }
  }, [deviceUrl, updateChat]);

  const handleAsk = async (overrideQuestion) => {
    const q = (overrideQuestion ?? question).trim();
    if (!q || asking || !deviceUrl || isReadOnly) return;

    let chatId = activeChatId;
    if (!chatId) {
      const chat = { id: uid(), title: truncate(q, 48), projectId: null, bookmarked: false, createdAt: Date.now(), updatedAt: Date.now(), messages: [] };
      setChats((prev) => [chat, ...prev]);
      setActiveChatId(chat.id);
      chatId = chat.id;
      persistChat(chat);
    }

    const msgId = uid();
    updateChat(chatId, (c) => ({
      ...c,
      title: c.messages.length === 0 ? truncate(q, 48) : c.title,
      messages: [...c.messages, { id: msgId, question: q, answer: '', sources: [], error: null }],
    }));
    setQuestion('');
    await sendQuestion(chatId, msgId, q);
  };

  const retryMessage = (msgId, q) => {
    if (asking || isReadOnly || !activeChatId) return;
    updateChat(activeChatId, (c) => ({
      ...c,
      messages: c.messages.map((m) => (m.id === msgId ? { ...m, error: null, answer: '' } : m)),
    }));
    sendQuestion(activeChatId, msgId, q);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAsk(); }
  };

  const toggleBookmark = (chatId, e) => {
    e?.stopPropagation();
    updateChat(chatId, (c) => ({ ...c, bookmarked: !c.bookmarked }));
  };

  const startRename = (chat, e) => {
    e?.stopPropagation();
    setEditingChatId(chat.id);
    setEditingTitle(chat.title);
    setMenuOpenFor(null);
  };

  const commitRename = () => {
    if (editingChatId && editingTitle.trim()) {
      updateChat(editingChatId, (c) => ({ ...c, title: editingTitle.trim() }));
    }
    setEditingChatId(null);
  };

  const removeChat = (chatId) => {
    const remaining = chats.filter((c) => c.id !== chatId);
    setChats(remaining);
    deleteChatDb(chatId).catch(() => {});
    if (activeChatId === chatId) setActiveChatId(remaining[0]?.id || null);
    setConfirmDeleteChat(null);
  };

  const moveChatToProject = (chatId, projectId, e) => {
    e?.stopPropagation();
    updateChat(chatId, (c) => ({ ...c, projectId }));
    setMoveMenuFor(null);
    setMenuOpenFor(null);
  };

  const createProject = () => {
    const name = newProjectName.trim();
    if (!name) return;
    const project = { id: uid(), name, createdAt: Date.now() };
    setProjects((prev) => [...prev, project].sort((a, b) => a.name.localeCompare(b.name)));
    saveProjectDb(userId, project).catch(() => {});
    setNewProjectName('');
    setShowNewProjectInput(false);
  };

  const removeProject = (projectId) => {
    setProjects((prev) => prev.filter((p) => p.id !== projectId));
    deleteProjectDb(projectId).catch(() => {});
    chats.filter((c) => c.projectId === projectId).forEach((c) => updateChat(c.id, (ch) => ({ ...ch, projectId: null })));
    setConfirmDeleteProject(null);
  };

  const filteredChats = useMemo(() => {
    if (!search.trim()) return chats;
    const q = search.toLowerCase();
    return chats.filter((c) =>
      c.title.toLowerCase().includes(q) ||
      c.messages.some((m) => m.question.toLowerCase().includes(q) || m.answer.toLowerCase().includes(q))
    );
  }, [chats, search]);

  const bookmarkedChats = filteredChats.filter((c) => c.bookmarked);
  const groupedUnfiled = groupChatsByDate(filteredChats.filter((c) => !c.projectId));

  const renderChatItem = (chat) => (
    <div
      key={chat.id}
      onClick={() => { setActiveChatId(chat.id); if (window.innerWidth <= 860) setSidebarOpen(false); }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') setActiveChatId(chat.id); }}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 10px',
        borderRadius: theme.radiusSm, cursor: 'pointer', position: 'relative',
        background: activeChatId === chat.id ? theme.accentSoft : 'transparent',
        border: `1px solid ${activeChatId === chat.id ? theme.accentBorder : 'transparent'}`,
        transition: 'background 0.12s ease',
      }}
      onMouseEnter={(e) => { if (activeChatId !== chat.id) e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
      onMouseLeave={(e) => { if (activeChatId !== chat.id) e.currentTarget.style.background = 'transparent'; }}
    >
      <FaComment style={{ fontSize: '11px', color: activeChatId === chat.id ? theme.accent : theme.textTertiary, flexShrink: 0 }} />
      {editingChatId === chat.id ? (
        <input
          autoFocus
          value={editingTitle}
          onChange={(e) => setEditingTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingChatId(null); }}
          onClick={(e) => e.stopPropagation()}
          aria-label="Rename chat"
          style={{ flex: 1, background: theme.bg, border: `1px solid ${theme.accent}`, borderRadius: '5px', color: 'white', fontSize: '0.85rem', padding: '3px 6px', minWidth: 0 }}
        />
      ) : (
        <span style={{
          flex: 1, fontSize: '0.85rem', color: activeChatId === chat.id ? theme.textPrimary : '#c6cddc',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {chat.title}
        </span>
      )}
      {!isReadOnly && (
        <>
          <button
            onClick={(e) => toggleBookmark(chat.id, e)}
            aria-label={chat.bookmarked ? 'Remove bookmark' : 'Bookmark chat'}
            title={chat.bookmarked ? 'Remove bookmark' : 'Bookmark chat'}
            style={{ background: 'none', border: 'none', color: chat.bookmarked ? theme.amber : theme.textTertiary, cursor: 'pointer', fontSize: '13px', padding: '2px', flexShrink: 0 }}
          >
            {chat.bookmarked ? <FaStar /> : <FaRegStar />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpenFor(menuOpenFor === chat.id ? null : chat.id); }}
            aria-label="Chat options"
            aria-haspopup="true"
            style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: '13px', padding: '2px', flexShrink: 0 }}
          >
            <FaEllipsisV />
          </button>
        </>
      )}

      {menuOpenFor === chat.id && (
        <div ref={menuRef} onClick={(e) => e.stopPropagation()} style={{
          position: 'absolute', right: 0, top: '34px', zIndex: 20, background: theme.panelSolid,
          border: `1px solid ${theme.borderStrong}`, borderRadius: theme.radiusMd, minWidth: '175px',
          boxShadow: '0 10px 28px rgba(0,0,0,0.45)', padding: '5px', animation: 'askaiPopIn 0.12s ease both',
        }}>
          <button onClick={(e) => startRename(chat, e)} style={menuItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          ><FaEdit /> Rename</button>
          <div style={{ position: 'relative' }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMoveMenuFor(moveMenuFor === chat.id ? null : chat.id); }}
              style={menuItemStyle}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <FaFolder /> Move to project
            </button>
            {moveMenuFor === chat.id && (
              <div style={{ position: 'absolute', right: '0%', top: '100%', background: theme.panelSolid, border: `1px solid ${theme.borderStrong}`, borderRadius: theme.radiusMd, minWidth: '160px', boxShadow: '0 10px 28px rgba(0,0,0,0.45)', padding: '5px' }}>
                <button onClick={(e) => moveChatToProject(chat.id, null, e)} style={menuItemStyle}>No project</button>
                {projects.map((p) => (
                  <button key={p.id} onClick={(e) => moveChatToProject(chat.id, p.id, e)} style={menuItemStyle}>{p.name}</button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteChat(chat.id); setMenuOpenFor(null); }}
            style={{ ...menuItemStyle, color: theme.danger }}
            onMouseEnter={(e) => (e.currentTarget.style.background = theme.dangerSoft)}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <FaTrash /> Delete
          </button>
        </div>
      )}
    </div>
  );

  // ── No device connected ──
  if (!deviceUrl) {
    return (
      <div className="askai-root"
       style={{
    position: 'relative',
    display: 'flex',
    height: '100%',
    width: '100%',
    margin: '0 auto',
    background: 'transparent',
  }}>
        <GlobalStyles />
        <div className="askai-empty-state" style={{
  textAlign: 'center', padding: '72px 40px', color: 'white',
  background: 'radial-gradient(120% 120% at 50% 0%, rgba(58,167,245,0.09), rgba(20,26,46,0.6))',
  borderRadius: theme.radiusLg, border: `1px solid ${theme.accentBorder}`, maxWidth: '620px', margin: '40px auto',
}}>
          <div style={{
            width: '64px', height: '64px', margin: '0 auto 22px', borderRadius: '16px',
            background: theme.gradient, display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(3,140,232,0.35)',
          }}>
            <FaMicrochip style={{ fontSize: '1.7rem', color: '#08111f' }} />
          </div>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: theme.textPrimary, fontWeight: 700 }}>Connect your AspireAI device</h2>
          <p style={{ fontSize: '1rem', lineHeight: 1.6, color: theme.textSecondary, marginBottom: '10px', maxWidth: '440px', marginLeft: 'auto', marginRight: 'auto' }}>
            Ask AI answers questions using the study materials you've uploaded to your local device.
          </p>
          <p style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.82rem', color: theme.accent,
            background: theme.accentSoft, border: `1px solid ${theme.accentBorder}`, borderRadius: '999px',
            padding: '5px 13px', marginBottom: '26px',
          }}>
            <FaLock size={10} /> Nothing leaves your network
          </p>
          <div>
            <button onClick={onOpenDeviceManager} style={{
              padding: '13px 26px', fontSize: '0.95rem', background: theme.gradient,
              border: 'none', borderRadius: theme.radiusSm, color: '#08111f', fontWeight: 700, cursor: 'pointer',
              boxShadow: '0 6px 18px rgba(3,140,232,0.3)',
            }}>
              Connect Device
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="askai-root" style={{ position: 'relative', display: 'flex', height: '100%', width: '100%', margin: '0 auto', background: theme.bg }}>
      <GlobalStyles />

      {/* ── Main chat column ── */}
<div className="askai-main-col" style={{
  flex: 1,
  maxWidth: sidebarOpen ? '1300px' : 'none',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        paddingLeft: '20px',
        paddingRight: '20px',
        paddingTop: '20px',
        transition: 'max-width 0.22s ease',
      }}>
        {/* Header */}
<div className="askai-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '10px' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
            <h2 style={{ margin: 0, color: theme.textPrimary, fontSize: '1.28rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeChat ? activeChat.title : 'Ask AspireAI'}
            </h2>
            <p style={{ margin: '5px 0 0', fontSize: '0.8rem', color: theme.textTertiary, display: 'flex', alignItems: 'center', gap: '5px' }}>
              <FaLock size={9} /> Answers enhanced by your uploaded materials, processed securely on-device
            </p>
          </div>
<div className="askai-header-actions" style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {!isReadOnly && (
              <button
                onClick={createNewChat}
                title="New chat"
                aria-label="New chat"
                style={{
                  background: 'rgba(255,255,255,0.04)', border: `1px solid ${theme.border}`, borderRadius: theme.radiusSm,
                  color: '#ccc', padding: '9px 11px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center',
                }}
              >
                <FaPlus />
              </button>
            )}
            {activeChat && !isReadOnly && (
              <button
                onClick={(e) => toggleBookmark(activeChat.id, e)}
                title={activeChat.bookmarked ? 'Remove bookmark' : 'Bookmark this chat'}
                aria-label={activeChat.bookmarked ? 'Remove bookmark' : 'Bookmark this chat'}
                style={{
                  background: activeChat.bookmarked ? theme.amberSoft : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${activeChat.bookmarked ? theme.amber : theme.border}`,
                  borderRadius: theme.radiusSm, color: activeChat.bookmarked ? theme.amber : '#888',
                  padding: '9px 11px', cursor: 'pointer', fontSize: '13px',
                }}
              >
                {activeChat.bookmarked ? <FaStar /> : <FaRegStar />}
              </button>
            )}
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              title={sidebarOpen ? 'Hide chat history' : 'Show chat history'}
              aria-label={sidebarOpen ? 'Hide chat history' : 'Show chat history'}
              aria-pressed={sidebarOpen}
              style={{
                background: sidebarOpen ? theme.accentSoft : 'rgba(255,255,255,0.04)',
                border: `1px solid ${sidebarOpen ? theme.accentBorder : theme.border}`,
                borderRadius: theme.radiusSm, color: sidebarOpen ? theme.accent : '#888',
                padding: '9px 11px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center',
              }}
            >
              <FaHistory />
            </button>
          </div>
        </div>

        {/* Conversation */}
<div ref={scrollRef} className="askai-scroll askai-conversation" style={{
            flex: 1, overflowY: 'auto', padding: '18px', display: 'flex', flexDirection: 'column', gap: '20px',
          background: theme.panel, border: `1px solid ${theme.border}`, borderRadius: theme.radiusLg,
        }}>
          {!activeChat || activeChat.messages.length === 0 ? (
            <div style={{ margin: 'auto', textAlign: 'center', color: theme.textSecondary, maxWidth: '560px' }}>
              <div style={{
                width: '52px', height: '52px', margin: '0 auto 16px', borderRadius: '14px',
                background: theme.accentSoft, border: `1px solid ${theme.accentBorder}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FaComment style={{ color: theme.accent, fontSize: '1.15rem' }} />
              </div>
              <p style={{ margin: '0 0 20px', fontSize: '0.95rem', lineHeight: 1.55, color: theme.textSecondary }}>
                Ask for summaries, key points, or exam prep — or just start typing below.
              </p>
              {!isReadOnly && (
                <div className="askai-suggestion" style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'center' }}>
                  <div className="askai-suggestion-row-label" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', maxWidth: '480px' }}>
                    <span className="askai-suggestion-row-label" style={{ width: '100%', fontSize: '0.72rem', fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                      From your materials
                    </span>
                    {[
                      'Summarise my most recent uploads',
                      'What are the main topics covered in my materials?',
                      'Generate a short quiz from my uploaded materials',
                    ].map((p) => (
                      <button
                      className='askai-bubble-prompt'
                        key={p}
                        onClick={() => handleAsk(p)}
                        style={{
                          padding: '8px 14px', background: theme.accentSoft, border: `1px solid ${theme.accentBorder}`,
                          borderRadius: '999px', color: theme.accent, fontSize: '0.82rem', cursor: 'pointer', transition: 'background 0.12s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(58,167,245,0.2)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = theme.accentSoft)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <div className="askai-suggestion-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', maxWidth: '480px' }}>
                    <span className="askai-suggestion-row-label" style={{ width: '100%', fontSize: '0.72rem', fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>
                      General
                    </span>
                    {[
                      'Help me revise the basics of algebra',
                      'Give me tips for effective exam revision',
                    ].map((p) => (
                      <button
                        className='askai-bubble-prompt'
                        key={p}
                        onClick={() => handleAsk(p)}
                        style={{
                          padding: '8px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${theme.border}`,
                          borderRadius: '999px', color: theme.textSecondary, fontSize: '0.82rem', cursor: 'pointer', transition: 'background 0.12s ease',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            activeChat.messages.map((entry) => (
              <ChatMessage key={entry.id} entry={entry} onRetry={retryMessage} isReadOnly={isReadOnly} />
            ))
          )}
        </div>

        {/* Composer */}
        {!isReadOnly && (
  <div className="askai-composer" style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '9px', alignItems: 'flex-end' }}>
              <textarea
                ref={textareaRef}
                className="askai-textarea"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                rows={1}
                placeholder="Ask anything…"
                aria-label="Ask a question"
                style={{
                  flex: 1, padding: '13px 14px', borderRadius: theme.radiusMd, border: `1px solid ${theme.border}`,
                  background: theme.panelSolid, color: 'white', resize: 'none', fontSize: '0.92rem',
                  maxHeight: '160px', overflowY: 'auto', lineHeight: 1.5, transition: 'border-color 0.15s ease',
                }}
                onFocus={(e) => { e.target.style.borderColor = theme.accentBorder; }}
                onBlur={(e) => { e.target.style.borderColor = theme.border; }}
              />
              <button
                onClick={() => handleAsk()}
                disabled={asking || !question.trim()}
                aria-label="Send question"
                style={{
                  padding: '13px 18px', background: asking || !question.trim() ? 'rgba(255,255,255,0.06)' : theme.gradient,
                  color: asking || !question.trim() ? theme.textTertiary : '#08111f',
                  border: 'none', borderRadius: theme.radiusMd, fontWeight: 700,
                  cursor: asking || !question.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '46px',
                }}
              >
                {asking ? (
                  <span style={{ display: 'inline-flex', gap: '3px' }} aria-hidden="true">
                    <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                    <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                    <span className="askai-dot" style={{ width: '4px', height: '4px', borderRadius: '50%', background: 'currentColor', display: 'inline-block' }} />
                  </span>
                ) : <FaArrowUp />}
              </button>
            </div>
            <p style={{ margin: '7px 2px 0', fontSize: '0.74rem', color: theme.textTertiary }}>
              Enter to send · Shift + Enter for a new line
            </p>
          </div>
        )}
      </div>

      {/* Mobile overlay backdrop */}
      {sidebarOpen && (
        <div className="askai-backdrop" onClick={() => setSidebarOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(6,9,18,0.55)', zIndex: 55 }} />
      )}

      {/* ── Right-hand sidebar (history / projects / bookmarks) ── */}
      <div className={`askai-sidebar${sidebarOpen ? ' open' : ''}`}>
        <div style={{ width: SIDEBAR_WIDTH, height: '100%', padding: '16px', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
          {/* Sidebar header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: theme.textPrimary }}>Chat History</span>
            <button
              onClick={() => setSidebarOpen(false)}
              title="Hide sidebar"
              aria-label="Hide sidebar"
              style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: '13px', padding: '4px' }}
            >
              <FaTimes />
            </button>
          </div>

          {!isReadOnly && (
            <button onClick={createNewChat} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              padding: '11px', background: theme.gradient, color: '#08111f',
              border: 'none', borderRadius: theme.radiusMd, fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', marginBottom: '14px',
              boxShadow: '0 4px 14px rgba(3,140,232,0.25)',
            }}>
              <FaPlus /> New Chat
            </button>
          )}

          <div style={{ position: 'relative', marginBottom: '16px' }}>
            <FaSearch style={{ position: 'absolute', left: '11px', top: '11px', color: theme.textTertiary, fontSize: '12px' }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search chats..."
              aria-label="Search chats"
              style={{
                width: '100%', padding: '9px 10px 9px 32px', background: theme.panelSolid, border: `1px solid ${theme.border}`,
                borderRadius: theme.radiusSm, color: 'white', fontSize: '0.83rem', boxSizing: 'border-box',
              }}
            />
          </div>

          <div className="askai-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* Bookmarked */}
            {bookmarkedChats.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.76rem', fontWeight: 700, color: theme.amber, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '7px', padding: '0 4px' }}>
                  <FaStar style={{ fontSize: '10px' }} /> Bookmarked
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  {bookmarkedChats.map(renderChatItem)}
                </div>
              </div>
            )}

            {/* Projects */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px', marginBottom: '7px' }}>
                <span style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Projects</span>
                {!isReadOnly && (
                  <button onClick={() => setShowNewProjectInput((v) => !v)} title="New project" aria-label="New project" style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', fontSize: '15px', marginRight: '-4px' }}>
                    <FaFolderPlus />
                  </button>
                )}
              </div>

              {showNewProjectInput && (
                <div style={{ display: 'flex', gap: '6px', marginBottom: '9px', padding: '0 2px' }}>
                  <input
                    autoFocus
                    value={newProjectName}
                    onChange={(e) => setNewProjectName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') createProject(); if (e.key === 'Escape') setShowNewProjectInput(false); }}
                    placeholder="Project name"
                    aria-label="New project name"
                    style={{ flex: 1, padding: '7px 9px', background: theme.panelSolid, border: `1px solid ${theme.accent}`, borderRadius: theme.radiusSm, color: 'white', fontSize: '0.8rem', minWidth: 0 }}
                  />
                  <button onClick={createProject} style={{ padding: '7px 12px', background: theme.gradient, border: 'none', borderRadius: theme.radiusSm, color: '#08111f', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}>Add</button>
                </div>
              )}

              {projects.length === 0 && !showNewProjectInput ? (
                <p style={{ fontSize: '0.83rem', color: theme.textTertiary, padding: '0 4px', fontStyle: 'italic' }}>No projects yet.</p>
              ) : (
                projects.map((project) => {
                  const projectChats = filteredChats.filter((c) => c.projectId === project.id);
                  const isCollapsed = collapsedProjects[project.id];
                  return (
                    <div key={project.id} style={{ marginBottom: '4px' }}>
                      <div
                        onClick={() => setCollapsedProjects((prev) => ({ ...prev, [project.id]: !prev[project.id] }))}
                        role="button"
                        tabIndex={0}
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 6px', cursor: 'pointer', borderRadius: theme.radiusSm }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      >
                        {isCollapsed ? <FaChevronRight style={{ fontSize: '10px', color: theme.textTertiary }} /> : <FaChevronDown style={{ fontSize: '10px', color: theme.textTertiary }} />}
                        <FaFolder style={{ fontSize: '14px', color: theme.violet }} />
                        <span style={{ flex: 1, fontSize: '0.83rem', color: '#dfe3ee', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {project.name}
                        </span>
                        <span style={{
                          fontSize: '0.72rem', color: theme.violet, background: theme.violetSoft,
                          borderRadius: '999px', padding: '1px 7px', fontWeight: 600,
                        }}>{projectChats.length}</span>
                        {!isReadOnly && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDeleteProject(project.id); }}
                            title="Delete project"
                            aria-label={`Delete project ${project.name}`}
                            style={{ background: 'none', border: 'none', color: theme.danger, cursor: 'pointer', fontSize: '12px', padding: '2px', opacity: 0.75 }}
                          >
                            <FaTrash />
                          </button>
                        )}
                      </div>
                      {!isCollapsed && (
                        <div style={{ paddingLeft: '15px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {projectChats.length === 0 ? (
                            <p style={{ fontSize: '0.8rem', color: theme.textTertiary, fontStyle: 'italic', padding: '4px 6px' }}>No chats filed here yet.</p>
                          ) : (
                            projectChats.map(renderChatItem)
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* All chats, grouped by date */}
            <div>
              <div style={{ fontSize: '0.76rem', fontWeight: 700, color: theme.textTertiary, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '7px', padding: '0 4px' }}>
                Chats
              </div>
              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '0 2px' }}>
                  {[0, 1, 2].map((i) => (
                    <div key={i} style={{ height: '34px', borderRadius: theme.radiusSm, background: 'rgba(255,255,255,0.04)' }} />
                  ))}
                </div>
              ) : groupedUnfiled.length === 0 ? (
                <p style={{ fontSize: '0.83rem', color: theme.textTertiary, padding: '0 4px', fontStyle: 'italic' }}>
                  {search ? 'No chats match your search.' : 'No chats yet — start one above.'}
                </p>
              ) : (
                groupedUnfiled.map(([label, list]) => (
                  <div key={label} style={{ marginBottom: '11px' }}>
                    <div style={{ fontSize: '0.78rem', color: theme.textTertiary, padding: '4px 6px' }}>{label}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {list.map(renderChatItem)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Footer — device connection */}
          <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: `1px solid ${theme.border}` }}>
            <button
              onClick={onOpenDeviceManager}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%', padding: '10px',
                background: 'rgba(255,255,255,0.03)', border: `1px solid ${theme.border}`, borderRadius: theme.radiusMd,
                cursor: 'pointer', textAlign: 'left', transition: 'background 0.12s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = theme.accentSoft)}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.03)')}
            >
              <span style={{
                width: '30px', height: '30px', borderRadius: '8px', background: theme.accentSoft,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.accent, flexShrink: 0,
              }}>
                <FaMicrochip size={13} />
              </span>
              <span style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '0.85rem', color: '#ccc', fontWeight: 600 }}>Manage Device</div>
                <div style={{ fontSize: '0.8rem', color: theme.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {deviceUrl.replace(/^https?:\/\//, '')}
                </div>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Floating reopen tab — appears only when the sidebar is collapsed */}
      {!sidebarOpen && (
        <button
          className='show-chat-history-button'
          onClick={() => setSidebarOpen(true)}
          title="Show chat history"
          aria-label="Show chat history"
          style={{
            position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
            width: '22px', height: '64px', background: theme.bgElevated, border: `1px solid ${theme.accentBorder}`,
            borderRight: 'none', borderRadius: '10px 0 0 10px', color: theme.accent, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '-2px 0 10px rgba(0,0,0,0.25)',
          }}
        >
          <FaChevronLeft size={11} />
        </button>
      )}

      {/* Delete chat confirm */}
      {confirmDeleteChat && (
        <ConfirmDialog
          title="Delete this chat?"
          description="This can't be undone."
          confirmLabel="Delete"
          tone="danger"
          onCancel={() => setConfirmDeleteChat(null)}
          onConfirm={() => removeChat(confirmDeleteChat)}
        />
      )}

      {/* Delete project confirm */}
      {confirmDeleteProject && (
        <ConfirmDialog
          title="Delete this project?"
          description={'Its chats will move back to "Chats" — they won\'t be deleted.'}
          confirmLabel="Delete"
          tone="amber"
          onCancel={() => setConfirmDeleteProject(null)}
          onConfirm={() => removeProject(confirmDeleteProject)}
        />
      )}
    </div>
  );
}
